import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import api, {
  isAuthError,
  isInvalidAccountSession,
  isMobileBrowser,
  isNetworkError,
  isSessionRevokedError,
  isTransientHttpError,
  refreshSession,
  clearMobileTokens,
  getMobileRefreshToken,
  saveMobileTokens,
} from "@/lib/api"
import { pinStaffHomeBranch } from "@/lib/branch-selection"
import { clearAllHeldSalesForUser } from "@/lib/pos-held-sales"

export interface AuthUser {
  id: number
  email: string | null
  username: string
  role: string | null
  roleId: number | null
  modules: string[]
  isActive: boolean
  systemRole?: "super_admin" | "tenant_user"
  isSuperAdmin?: boolean
  isTenantAdmin?: boolean
  homeBranchId?: number | null
  homeBranchName?: string | null
  mainBranchId?: number | null
  tenantId?: number | null
  localDemoShop?: boolean
  tenantSlug?: string | null
  tenantName?: string | null
  tenantStatus?: string | null
  trialEndsAt?: string | null
  trialConsumed?: boolean
  features?: {
    multiBranch?: boolean
    trackSerialNo: boolean
    specialPrice: boolean
    trackBatchNo: boolean
    trackExpiry: boolean
    allowNegativeQty: boolean
    allowSellBelowCost?: boolean
    buySellUnit: boolean
    tradePrice: boolean
    wholesalePrice: boolean
    resellerPrice: boolean
    variants?: boolean
    touchPos?: boolean
    /** When false, hide classic POS Register (SA-only). Defaults to true. */
    posRegister?: boolean
    /** When false, hide Sales from the sidebar (SA-only). Defaults to true. */
    salesSidebar?: boolean
    /** When false, hide Purchase from the sidebar (SA-only). Defaults to true. */
    purchaseSidebar?: boolean
    /** Super Admin only — Staff Meals tab on Touch POS. */
    staffMeals?: boolean
    quantityDecimalPlaces?: number
    amountDecimalPlaces?: number
  }
}

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  backendUnavailable: boolean
  login: (email: string, password: string, turnstileToken?: string) => Promise<AuthUser>
  register: (email: string, username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  hasModule: (module: string) => boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Access tokens expire in 15m — refresh proactively well before expiry.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function withRetry<T>(fn: () => Promise<T>, attempts = 4, delayMs = 800): Promise<T> {
  let lastError: unknown
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      if (!isNetworkError(err) || i === attempts - 1) throw err
      await sleep(delayMs * (i + 1))
    }
  }
  throw lastError
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [backendUnavailable, setBackendUnavailable] = useState(false)
  const userRef = useRef<AuthUser | null>(null)

  useEffect(() => {
    userRef.current = user
    if (user) pinStaffHomeBranch(user)
  }, [user])

  const lastRefreshRef = useRef<number>(0)
  const isRefreshingRef = useRef<boolean>(false)

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await withRetry(() => api.get("/api/auth/me"))
      setUser(data.user)
      setBackendUnavailable(false)
    } catch (err) {
      if (isNetworkError(err) || isTransientHttpError(err)) {
        setBackendUnavailable(true)
      } else if (isSessionRevokedError(err) || isInvalidAccountSession(err)) {
        setUser(null)
        setBackendUnavailable(false)
      } else if (isAuthError(err)) {
        // If the user was already authenticated, attempt an explicit refresh before resetting user state
        if (userRef.current) {
          try {
            await refreshSession()
            const { data } = await api.get("/api/auth/me")
            if (data?.user) {
              setUser(data.user)
              setBackendUnavailable(false)
              return
            }
          } catch (retryErr) {
            if (isNetworkError(retryErr) || isTransientHttpError(retryErr)) {
              setBackendUnavailable(true)
              return
            }
            if (isSessionRevokedError(retryErr)) {
              setUser(null)
              setBackendUnavailable(false)
              return
            }
          }
        }
        setUser(null)
        setBackendUnavailable(false)
      } else {
        setBackendUnavailable(true)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  const silentRefresh = useCallback(async () => {
    const now = Date.now()
    if (now - lastRefreshRef.current < 60_000 || isRefreshingRef.current) return
    isRefreshingRef.current = true
    try {
      await refreshSession()
      lastRefreshRef.current = Date.now()
      const meRes = await api.get("/api/auth/me")
      if (meRes.data?.user) setUser(meRes.data.user)
      setBackendUnavailable(false)
    } catch (err) {
      if (isNetworkError(err) || isTransientHttpError(err)) {
        setBackendUnavailable(true)
      } else if (isSessionRevokedError(err)) {
        setUser(null)
      }
      // Never eagerly log out on background silent refresh failure unless explicitly revoked.
      // Background timer throttling or sleep shouldn't kick the user out.
      // Next user interaction or active request will refresh tokens via Axios interceptor.
    } finally {
      isRefreshingRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!user) return

    const interval = setInterval(() => {
      void silentRefresh()
    }, REFRESH_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [user, silentRefresh])

  // When tab becomes visible again after being throttled in the background:
  // Re-validate session smoothly without disrupting active state.
  useEffect(() => {
    if (!user) return

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now()
        // Only re-check if at least 30s have passed since last refresh
        if (now - lastRefreshRef.current >= 30_000) {
          void fetchUser()
        }
      }
    }

    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [user, fetchUser])

  // Poll more often while the user only has dashboard access — likely stale after a restore.
  useEffect(() => {
    if (!user) return
    const moduleCount = user.modules?.length ?? 0
    if (moduleCount > 1) return

    const interval = setInterval(() => {
      void fetchUser()
    }, 30_000)

    return () => clearInterval(interval)
  }, [user, fetchUser])

  useEffect(() => {
    if (!backendUnavailable) return
    const interval = setInterval(() => fetchUser(), 3000)
    return () => clearInterval(interval)
  }, [backendUnavailable, fetchUser])

  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    const { data } = await api.post("/api/auth/login", { email, password, turnstileToken })
    if (isMobileBrowser() && data.accessToken && data.refreshToken) {
      saveMobileTokens(data.accessToken, data.refreshToken)
    } else {
      clearMobileTokens()
    }
    lastRefreshRef.current = Date.now()
    setUser(data.user)
    setBackendUnavailable(false)
    return data.user as AuthUser
  }, [])

  const register = useCallback(async (email: string, username: string, password: string) => {
    const { data } = await api.post("/api/auth/register", { email, username, password })
    if (isMobileBrowser() && data.accessToken && data.refreshToken) {
      saveMobileTokens(data.accessToken, data.refreshToken)
    } else {
      clearMobileTokens()
    }
    setUser(data.user)
    setBackendUnavailable(false)
  }, [])

  const logout = useCallback(async () => {
    const tenantId = user?.tenantId ?? null
    try {
      const mobile = isMobileBrowser()
      await api.post("/api/auth/logout", mobile ? { refreshToken: getMobileRefreshToken() } : undefined)
    } finally {
      clearMobileTokens()
      clearAllHeldSalesForUser(tenantId)
      setUser(null)
      setBackendUnavailable(false)
    }
  }, [user?.tenantId])

  const hasModule = useCallback((module: string) => user?.modules?.includes(module) ?? false, [user])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        backendUnavailable,
        login,
        register,
        logout,
        hasModule,
        refreshUser: fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider")
  return ctx
}
